import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { getOrderHistory } from '../../services/api';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

const OrderTimelineModal = ({ open, onClose, order }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchHistory = useCallback(async () => {
    if (!order) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await getOrderHistory(order.id);
      setHistory(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch order history');
    } finally {
      setLoading(false);
    }
  }, [order]);

  useEffect(() => {
    if (open && order) {
      fetchHistory();
    } else {
      setHistory([]);
      setError('');
    }
  }, [open, order, fetchHistory]);

  const getStatusLabel = (statusName) => {
    const statusMap = {
      'received': 'Received',
      'dispatched': 'Dispatched',
      'delivered': 'Delivered',
      'pending_sms_confirmation': 'Pending SMS Confirmation'
    };
    return statusMap[statusName] || statusName;
  };

  const getStatusIcon = (statusName) => {
    switch (statusName?.toLowerCase()) {
      case 'received':
        return <ShoppingCartIcon />;
      case 'dispatched':
        return <LocalShippingIcon />;
      case 'delivered':
        return <CheckCircleIcon />;
      default:
        return <AccessTimeIcon />;
    }
  };

  const getStatusColor = (statusName) => {
    switch (statusName?.toLowerCase()) {
      case 'received':
        return 'warning';
      case 'dispatched':
        return 'info';
      case 'delivered':
        return 'success';
      default:
        return 'grey';
    }
  };

  // Build timeline events
  const timelineEvents = [];
  
  // Add order placed event
  if (order?.createdAt) {
    timelineEvents.push({
      type: 'placed',
      label: 'Order Placed',
      timestamp: order.createdAt,
      user: null,
      icon: <ShoppingCartIcon />,
      color: 'primary'
    });
  }

  // Add status change events from history, deduplicating by status
  // Keep only the most recent entry for each unique status
  const statusMap = new Map();
  history.forEach((log) => {
    const statusName = log.OrderStatus?.status_name;
    if (!statusName) return;
    
    const existingEntry = statusMap.get(statusName);
    const logTimestamp = new Date(log.created_at);
    
    // If no existing entry or this one is more recent, update the map
    if (!existingEntry || logTimestamp > new Date(existingEntry.timestamp)) {
      statusMap.set(statusName, {
        type: 'status_change',
        label: getStatusLabel(statusName),
        timestamp: log.created_at,
        user: log.User?.name || log.User?.username || 'System',
        statusName: statusName,
        icon: getStatusIcon(statusName),
        color: getStatusColor(statusName)
      });
    }
  });
  
  // Add deduplicated status events to timeline
  statusMap.forEach((event) => {
    timelineEvents.push(event);
  });

  // Add delivered timestamp if available
  if (order?.delivered_at) {
    const hasDeliveredInHistory = history.some(
      log => log.OrderStatus?.status_name?.toLowerCase() === 'delivered'
    );
    
    if (!hasDeliveredInHistory) {
      timelineEvents.push({
        type: 'delivered',
        label: 'Delivered',
        timestamp: order.delivered_at,
        user: order.received_by || null,
        icon: <CheckCircleIcon />,
        color: 'success'
      });
    }
  }

  // Sort by timestamp
  timelineEvents.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (!order) return null;

  return (
    <AppDialog open={open} onClose={onClose} maxWidth="md">
      <AppDialogTitle onClose={onClose} subtitle={`Waybill: ${order.waybill_no}`}>
        Order Timeline
      </AppDialogTitle>
      <AppDialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : timelineEvents.length === 0 ? (
          <Alert severity="info">No timeline data available for this order.</Alert>
        ) : (
          <Box>
            {timelineEvents.map((event, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Box display="flex" alignItems="flex-start" gap={2}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: event.color === 'warning' ? 'warning.main' :
                               event.color === 'info' ? 'info.main' :
                               event.color === 'success' ? 'success.main' :
                               event.color === 'primary' ? 'primary.main' :
                               'grey.500',
                      color: 'white',
                      flexShrink: 0
                    }}
                  >
                    {event.icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {event.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {formatDateTime(event.timestamp)}
                    </Typography>
                    {event.user && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Updated by: {event.user}
                      </Typography>
                    )}
                    {event.type === 'delivered' && order.received_by && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Received by: {order.received_by}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {index < timelineEvents.length - 1 && (
                  <Box sx={{ ml: 3, mt: 1, mb: 1 }}>
                    <Divider orientation="vertical" sx={{ height: 20, borderLeftWidth: 2 }} />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </AppDialogContent>
      <AppDialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">Close</Button>
      </AppDialogActions>
    </AppDialog>
  );
};

export default OrderTimelineModal;

